/**
 * A pilot's status.
 *
 * The name is always rendered — PRD §9.7 does not permit status by colour
 * alone, and these five dots sit on one hue ramp that is not distinguishable to
 * many readers and identical in greyscale.
 *
 * @see docs/plan/IMPLEMENTATION_PLAN.md task 7.2, PRD §5.4, §9.7
 */
import { useTranslation } from 'react-i18next';

import { cn } from '../../lib/cn';

import { PILOT_STATUS_DOT, type PilotStatus } from './pilot-status';

export interface PilotStatusBadgeProps {
  status: PilotStatus;
  className?: string;
}

export function PilotStatusBadge({
  status,
  className,
}: PilotStatusBadgeProps): React.JSX.Element {
  const { t } = useTranslation();

  return (
    <span
      className={cn(
        'bg-gray-100 text-navy-text inline-flex items-center gap-[6px] rounded-full',
        'font-display px-[11px] py-1 text-[10px] leading-[1.5] font-extrabold',
        'tracking-[0.1em] whitespace-nowrap uppercase',
        className,
      )}
    >
      {/* Decorative: the label beside it already names the status. */}
      <span
        aria-hidden="true"
        className={cn('size-[7px] rounded-full', PILOT_STATUS_DOT[status])}
      />
      {t(`pilot.status.${status}`)}
    </span>
  );
}
